import { CostData } from '../modules/NewSolutionXPLR/OpportunityReport/FeasabilityAndImpact';
import { KeyInformationData } from '../modules/NewSolutionXPLR/SolutionXPLRInterfaces';
import { BenefitMeasure, IUseCase } from './types/solution';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import CodeIcon from '@mui/icons-material/Code';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Box } from '@mui/material';
import dayjs from 'dayjs';

export function splitAndCapitalize(input: string, delimiter: string) {
  if (typeof input !== 'string' || typeof delimiter !== 'string') {
    return '';
  }

  return input
    .split(delimiter)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const formatMessageWithBold = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g); // split and preserve **...**
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <span key={index} style={{ fontWeight: 600 }}>
          {part.slice(2, -2)}
        </span>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

const IDEA_STATEMENT_KEYS = [
  'aiIdea',
  'title',
  'use_case',
  'opportunity_statement',
  'name',
  'label',
];

const extractIdeaStatementValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of IDEA_STATEMENT_KEYS) {
      if (!Object.prototype.hasOwnProperty.call(record, key)) {
        continue;
      }
      const nestedResult = extractIdeaStatementValue(record[key]);
      if (nestedResult) {
        return nestedResult;
      }
    }
  }

  return null;
};

/**
 * Normalizes an idea statement coming from heterogeneous API payloads.
 * Safely unwraps nested structures so React never receives a raw object as a child.
 */
export const normalizeIdeaStatement = (input: unknown): string => {
  return extractIdeaStatementValue(input) ?? '';
};

export const getBenefitTitle = (key: string): string => {
  const map: Record<string, string> = {
    cost_savings: 'Cost Savings',
    revenue_growth: 'Revenue Growth',
    customer_experience: 'Customer Experience',
    process_productivity: 'Process Productivity',
    employee_productivity: 'Employee Productivity',
  };
  return map[key] || key;
};

export const getBenefitLabel = (value: number): string => {
  if (value >= 8) return 'High';
  if (value >= 5) return 'Medium';
  return 'Low';
};

export const getBenefitLabelNew = (value: string): string => {
  if (!value) return 'LOW'; // default if missing
  return value.toUpperCase(); // always "HIGH" | "MEDIUM" | "LOW"
};

export const getBenefitClassName = (value: number): string => {
  if (value >= 8) return 'background-high';
  if (value >= 5) return 'background-medium';
  return 'background-low';
};

export const formatTimestamp = (dateString: string): string => {
  if (!dateString) return '-';

  // Parse the date string as-is (already in local time from backend)
  const date = dayjs(dateString);

  if (!date.isValid()) return '-';

  const now = dayjs();

  const diffMs = now.valueOf() - date.valueOf();
  const diffMins = Math.floor(diffMs / (1000 * 60));

  // Just now (within 1 minute)
  if (diffMins < 1) {
    return 'Just now';
  }

  // X min ago (within 1 hour)
  if (diffMins < 60) {
    return `${diffMins} min ago`;
  }

  const day = date.format('DD');
  const month = date.format('MMM');
  const hours = date.format('hh');
  const minutes = date.format('mm');
  const ampm = date.format('A');

  return `${day} ${month}, ${hours}:${minutes} ${ampm}`;
};

// create a new formateTimestampToDate funtion which will return the date in mm/dd/yyyy and also use time formatting logic from formatTimestamp function
export const formatTimestampToDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Removes HTML tags and checks if only whitespace is left
export const isContentEmpty = (content: string): boolean => {
  if (!content) return true;
  const textOnly = content
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
    .trim();

  return textOnly.length === 0;
};

// Generic debounce utility
export const debouncedUpdateField = (
  field: keyof KeyInformationData,
  value: string | number | string[],
  debounceTimeouts: React.MutableRefObject<{ [key: string]: NodeJS.Timeout }>,
  keyInformationRef: React.MutableRefObject<KeyInformationData>,
  setKeyInformationData: React.Dispatch<React.SetStateAction<KeyInformationData>>,
  handleAutoSave?: (data: KeyInformationData) => void,
  debounceDelay: number = 500,
  immediate: boolean = false
) => {
  const fieldKey = field as string;

  if (debounceTimeouts.current[fieldKey]) {
    clearTimeout(debounceTimeouts.current[fieldKey]);
  }

  const newData: KeyInformationData = {
    ...keyInformationRef.current,
    [field]: value,
  };

  setKeyInformationData(newData);
  keyInformationRef.current = newData;

  if (!handleAutoSave) return;

  if (immediate) {
    handleAutoSave(newData);
  } else {
    debounceTimeouts.current[fieldKey] = setTimeout(() => {
      handleAutoSave(keyInformationRef.current);
      delete debounceTimeouts.current[fieldKey];
    }, debounceDelay);
  }
};

export function formatWithSuffix(value: number): string {
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  let suffixIndex = 0;
  const isNegative = value < 0;
  value = Math.abs(value);

  while (value >= 1000 && suffixIndex < suffixes.length - 1) {
    value /= 1000;
    suffixIndex++;
  }

  const formattedValue =
    value % 1 === 0
      ? value.toFixed(0)
      : value
          .toFixed(1)
          .replace(/\.0$/, '')
          .replace(/(\.\d)0$/, '$1');

  // Currency symbol ke saath negative sign properly place karna
  return `${isNegative ? '-' : ''}${formattedValue}${suffixes[suffixIndex]}`;
}

export const removeEmojisFromHtml = (html: string): string => {
  const doc = document.createElement('div');
  doc.innerHTML = html;

  // More targeted emoji regex that excludes business symbols
  // This targets common decorative emojis but preserves business symbols like ®, ™, ©, etc.
  const decorativeEmojiRegex =
    /[\u2600-\u26FF\u2700-\u27BF\uD83C-\uD83E][\uDC00-\uDFFF]?|[\u2713\u2714\u2716\u2717\u2705\u274C\u274E\u2B50\u2B55\u2796-\u27B0\u2B05-\u2B07\u2B1B\u2B1C]/g;

  const treeWalker = document.createTreeWalker(doc, NodeFilter.SHOW_TEXT, null);

  while (treeWalker.nextNode()) {
    const node = treeWalker.currentNode as Text;
    if (node.nodeValue) {
      // Remove decorative emojis while preserving business symbols
      node.nodeValue = node.nodeValue.replace(decorativeEmojiRegex, '');
    }
  }

  return doc.innerHTML;
};

// Alternative implementation with more precise control
export const removeEmojisFromHtmlPrecise = (html: string): string => {
  const doc = document.createElement('div');
  doc.innerHTML = html;

  // Define specific emoji characters to remove
  const emojisToRemove = [
    '✅',
    '❌',
    '✔️',
    '❎',
    '⭐',
    '🔥',
    '💡',
    '📈',
    '📊',
    '🎯',
    '🚀',
    '💰',
    '📋',
    '📝',
    '🔍',
    '⚡',
    '🎉',
    '👍',
    '👎',
    '🔒',
    '🔓',
    '⚠️',
    '❗',
    '❓',
    '💼',
    '🏢',
    '📱',
    '💻',
    '🖥️',
    '⌚',
  ];

  // Create regex from specific emojis to remove
  const specificEmojiRegex = new RegExp(
    emojisToRemove.map((emoji) => emoji.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
    'g'
  );

  const treeWalker = document.createTreeWalker(doc, NodeFilter.SHOW_TEXT, null);

  while (treeWalker.nextNode()) {
    const node = treeWalker.currentNode as Text;
    if (node.nodeValue) {
      node.nodeValue = node.nodeValue.replace(specificEmojiRegex, '');
    }
  }

  return doc.innerHTML;
};

export const removeEmojisFromHtmlOptimal = (html: string): string => {
  const doc = document.createElement('div');
  doc.innerHTML = html;

  // Define business symbols that should be preserved
  const businessSymbols = /[®™©§¶†‡•‰‱′″‴°±×÷≈≠≤≥∞√∑∏∂∆∇∈∉∋∌∩∪⊂⊃⊆⊇⊕⊖⊗⊘α-ωΑ-Ω]/;

  // Comprehensive emoji regex that catches all emoji ranges
  const allEmojisRegex =
    /([\u203C-\u3299]|[\u1F000-\u1FFFF]|[\u2100-\u27BF]|[\uFE00-\uFE0F]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDDFF])/g;

  const treeWalker = document.createTreeWalker(doc, NodeFilter.SHOW_TEXT, null);

  while (treeWalker.nextNode()) {
    const node = treeWalker.currentNode as Text;
    if (node.nodeValue) {
      // Remove all emojis except business symbols
      node.nodeValue = node.nodeValue.replace(allEmojisRegex, (match) => {
        // Keep the character if it's a business symbol, otherwise remove it
        return businessSymbols.test(match) ? match : '';
      });
    }
  }

  return doc.innerHTML;
};
//   return `${isNegative ? '-' : ''}${formattedValue}${suffixes[suffixIndex]}`;
// }

// Calculate Cost Savings difference
export function calculateSavingsDifference(costSavingsData: BenefitMeasure[]): number {
  let totalCurrentBaseline = 0;
  let totalTargetState = 0;

  costSavingsData?.forEach((benefit) => {
    if (benefit.label === 'Cost Reduction') {
      totalCurrentBaseline += benefit.currentBaseline;
      totalTargetState += benefit.targetState;
    }
  });

  if (totalCurrentBaseline !== 0 && totalTargetState !== 0) {
    return Math.abs(totalTargetState - totalCurrentBaseline);
  }

  return 0;
}

// Calculate Working Capital difference
export function calculateWorkingCapitalDifference(
  workingCapital: BenefitMeasure[],
  revenueGrowthBenefits: BenefitMeasure[]
): number {
  let dso = 0; // Days Sales Outstanding
  let dpo = 0; // Days Payable Outstanding
  let dio = 0; // Days Inventory Outstanding

  workingCapital?.forEach((benefit) => {
    if (benefit.label === 'Days Sales Outstanding (DSO)') {
      dso += benefit.targetState - benefit.currentBaseline;
    } else if (benefit.label === 'Days Payable Outstanding (DPO)') {
      dpo += benefit.targetState - benefit.currentBaseline;
    } else if (benefit.label === 'Days Inventory Outstanding (DIO)') {
      dio += benefit.targetState - benefit.currentBaseline;
    }
  });

  const revenueGrowth = calculateRevenueDifference(revenueGrowthBenefits);
  const workingCapitalDifference = (dso + dio - dpo) * (revenueGrowth / 365);

  return workingCapitalDifference;
}

// Calculate Customer Experience difference
export function calculateCustomerDifference(customerExperience: BenefitMeasure[]): number {
  let totalDifference = 0;

  customerExperience?.forEach((benefit) => {
    if (benefit.label === 'Customer Satisfaction') {
      totalDifference += Math.abs(benefit.targetState - benefit.currentBaseline);
    }
  });

  return parseFloat(totalDifference.toFixed(2));
}

// Calculate Employee Capacity
export function calculateEmployeeCapacity(
  employeeProductivity: BenefitMeasure[],
  costSavings: BenefitMeasure[]
): { total: number; percentTotal: number } {
  let totalCapacity = 0;
  let totalCurrentBaseline = 0;
  let hasValidValues = false;

  const FTEs = costSavings?.find((benefit) => benefit.label === 'FTEs');

  if (FTEs) {
    employeeProductivity?.forEach((benefit) => {
      if (
        benefit.label === 'Capacity Creation (hours)' &&
        benefit.targetState !== 0 &&
        benefit.currentBaseline !== 0
      ) {
        totalCapacity += (benefit.currentBaseline - benefit.targetState) * FTEs.currentBaseline;
        totalCurrentBaseline += benefit.currentBaseline;
        hasValidValues = true;
      }
    });
  }

  if (hasValidValues && totalCurrentBaseline !== 0) {
    const percentTotal = (totalCapacity / totalCurrentBaseline) * 100;
    return {
      total: Math.abs(totalCapacity),
      percentTotal: parseFloat(percentTotal.toFixed(2)),
    };
  }

  return { total: 0, percentTotal: 0 };
}

// Calculate Revenue difference
export function calculateRevenueDifference(revenueGrowth: BenefitMeasure[]): number {
  const revenueBenefit = revenueGrowth?.find((benefit) => benefit.label === 'Revenue');
  if (revenueBenefit) {
    const targetState = revenueBenefit.targetState || 0;
    const currentBaseline = revenueBenefit.currentBaseline || 0;
    if (targetState === 0 && currentBaseline === 0) {
      return 0;
    }
    return targetState - currentBaseline;
  }
  return 0;
}

// Enhanced calculation for total difference including all categories
export function calculateTotalDifference(
  revenueGrowth: BenefitMeasure[] = [],
  costSavingsData: BenefitMeasure[] = []
): number {
  const revenueDifference = calculateRevenueDifference(revenueGrowth);
  const savingsDifference = calculateSavingsDifference(costSavingsData);

  const customRevenue = revenueGrowth
    .filter((b) => !b.default && b.symbol === '$')
    .reduce((acc, b) => acc + Math.abs(b.targetState - b.currentBaseline), 0);

  const customCost = costSavingsData
    .filter((b) => !b.default && b.symbol === '$')
    .reduce((acc, b) => acc + Math.abs(b.targetState - b.currentBaseline), 0);

  return Math.abs(revenueDifference) + Math.abs(savingsDifference) + customRevenue + customCost;
}
//export function getBenefitValue(benefitsValues: IUseCase['benefitsValues'], year?: number): number {
//   if (!benefitsValues) return 0;
// Enhanced getBenefitValue with all categories calculation
export function getBenefitValue(benefitsValues: IUseCase['benefitsValues'], year?: number): number {
  if (!benefitsValues) {
    return 0;
  }

  // Ensure all categories exist as arrays
  const defaultBenefitsValues: IUseCase['benefitsValues'] = {
    ...benefitsValues,
    revenue_growth: benefitsValues?.revenue_growth ?? [],
    working_capital: benefitsValues?.working_capital ?? [],
    customer_experience: benefitsValues?.customer_experience ?? [],
    process_productivity: benefitsValues?.process_productivity ?? [],
    employee_productivity: benefitsValues?.employee_productivity ?? [],
    cost_savings: benefitsValues?.cost_savings ?? [],
  };
  // If year not specified, calculate total across all categories
  if (!year) {
    const revenueDifference = calculateRevenueDifference(
      defaultBenefitsValues.revenue_growth || []
    );
    const savingsDifference = calculateSavingsDifference(defaultBenefitsValues.cost_savings || []);
    const workingCapitalDifference = calculateWorkingCapitalDifference(
      defaultBenefitsValues.working_capital || [],
      defaultBenefitsValues.revenue_growth || []
    );

    // ✅ Always use `|| []` before filter/reduce
    const customRevenue = (defaultBenefitsValues.revenue_growth || [])
      .filter((b) => !b.default && b.symbol === '$')
      .reduce((acc, b) => acc + Math.abs((b.targetState ?? 0) - (b.currentBaseline ?? 0)), 0);

    const customCost = (defaultBenefitsValues.cost_savings || [])
      .filter((b) => !b.default && b.symbol === '$')
      .reduce((acc, b) => acc + Math.abs((b.targetState ?? 0) - (b.currentBaseline ?? 0)), 0);

    const customWorkingCapital = (defaultBenefitsValues.working_capital || [])
      .filter((b) => !b.default && b.symbol === '$')
      .reduce((acc, b) => acc + Math.abs((b.targetState ?? 0) - (b.currentBaseline ?? 0)), 0);

    const customCustomerExperience = (defaultBenefitsValues.customer_experience || [])
      .filter((b) => !b.default && b.symbol === '$')
      .reduce((acc, b) => acc + Math.abs((b.targetState ?? 0) - (b.currentBaseline ?? 0)), 0);

    const customProcessProductivity = (defaultBenefitsValues.process_productivity || [])
      .filter((b) => !b.default && b.symbol === '$')
      .reduce((acc, b) => acc + Math.abs((b.targetState ?? 0) - (b.currentBaseline ?? 0)), 0);

    const customEmployeeProductivity = (defaultBenefitsValues.employee_productivity || [])
      .filter((b) => !b.default && b.symbol === '$')
      .reduce((acc, b) => acc + Math.abs((b.targetState ?? 0) - (b.currentBaseline ?? 0)), 0);

    const total =
      Math.abs(revenueDifference) +
      Math.abs(savingsDifference) +
      Math.abs(workingCapitalDifference) +
      customRevenue +
      customCost +
      customWorkingCapital +
      customCustomerExperience +
      customProcessProductivity +
      customEmployeeProductivity;

    console.log('Total benefit calculation:', {
      revenueDifference,
      savingsDifference,
      workingCapitalDifference,
      customRevenue,
      customCost,
      customWorkingCapital,
      customCustomerExperience,
      customProcessProductivity,
      customEmployeeProductivity,
      total,
    });

    return total || 0; // ✅ fallback
  }

  // Filter by year if specified
  const yearString = `${year} Year`;

  function filterByYear(list: BenefitMeasure[] = []): BenefitMeasure[] {
    return list.filter((item) => (item.timeFrame || '').trim() === yearString);
  }

  const yearFilteredBenefits = {
    revenue_growth: filterByYear(defaultBenefitsValues.revenue_growth),
    working_capital: filterByYear(defaultBenefitsValues.working_capital),
    customer_experience: filterByYear(defaultBenefitsValues.customer_experience),
    process_productivity: filterByYear(defaultBenefitsValues.process_productivity),
    employee_productivity: filterByYear(defaultBenefitsValues.employee_productivity),
    cost_savings: filterByYear(defaultBenefitsValues.cost_savings),
  };

  return getBenefitValue(yearFilteredBenefits); // Recursive call without year
}

// Check if benefits have non-zero values
export function hasNonZeroValues(benefits: BenefitMeasure[] | undefined): boolean {
  return benefits
    ? benefits.some((data) => data.targetState !== 0 || data.currentBaseline !== 0)
    : false;
}

export function getEstimatedCostValueFromCostData(
  costData: CostData,
  duration?: '1yr' | '3yr' | '5yr'
): number {
  const extractMaxValue = (range: string): number => {
    if (!range) return 0;

    const parts = range.replace(/[$,]/g, '').split(/[-–]/);
    const lastPart = parts[parts.length - 1].trim();
    const value = parseFloat(lastPart);

    if (isNaN(value)) return 0;

    if (lastPart.includes('K')) return value * 1000;
    if (lastPart.includes('M')) return value * 1000000;
    return value;
  };

  const poc = extractMaxValue(costData.pocMax || costData.poc || '');
  const scaled = extractMaxValue(costData.scaledMax || costData.scaled || '');
  const maintenance = extractMaxValue(costData.maintenance || costData.maintenance || '');

  let totalCost = poc + scaled;

  if (duration) {
    switch (duration) {
      case '3yr':
        totalCost -= maintenance * 2;
        break;
      case '5yr':
        totalCost -= maintenance * 4;
        break;
      case '1yr':
      default:
        break;
    }
  }

  return totalCost;
}

export function getROIValue(benefit: number, cost: number): number {
  if (!benefit || !cost) return 0;

  const roi = (benefit / cost) * 100;

  return parseFloat(roi.toFixed(0));
}

export function evaluateBenefitExpression(data: BenefitMeasure, categoryName: string): string {
  try {
    const absoluteDifference = data.targetState - data.currentBaseline;
    const percentDifference =
      data.currentBaseline !== 0
        ? ((data.targetState - data.currentBaseline) / data.currentBaseline) * 100
        : 0;
    const increaseOrDecrease = percentDifference >= 0 ? 'increase' : 'decrease';

    if (data.symbol === '$') {
      return `$${formatWithSuffix(absoluteDifference)} in ${categoryName} (${
        percentDifference >= 0 ? '+' : '-'
      }${
        Number.isInteger(percentDifference)
          ? Math.abs(percentDifference).toFixed(0)
          : Math.abs(percentDifference).toFixed(1)
      }%)`;
    } else if (data.symbol === '%') {
      return `${percentDifference >= 0 ? '+' : '-'}${
        Number.isInteger(percentDifference)
          ? Math.abs(percentDifference).toFixed(0)
          : Math.abs(percentDifference).toFixed(1)
      }% ${increaseOrDecrease} in ${data.label} to ${absoluteDifference} for ${categoryName}`;
    } else if (data.symbol === '#') {
      return `${absoluteDifference} ${data.measure.toLowerCase()} ${
        absoluteDifference >= 0 ? 'increase' : 'decrease'
      } in ${data.label} in ${categoryName} (${percentDifference >= 0 ? '+' : '-'}${
        Number.isInteger(percentDifference)
          ? Math.abs(percentDifference).toFixed(0)
          : Math.abs(percentDifference).toFixed(1)
      }%)`;
    } else {
      return 'Invalid measure';
    }
  } catch (error) {
    console.error('Error calculating expression:', error);
    return 'Error';
  }
}

// Get benefit item for display
export function getBenefitItem(
  data: BenefitMeasure,
  categoryName: string
): {
  label: string;
  label_value: string;
  class_value: string;
  label_type_value: string;
  linear_class: string;
} {
  try {
    const absoluteDifference = Math.abs(data.targetState - data.currentBaseline);
    const percentDifference = (absoluteDifference / data.currentBaseline) * 100;

    if (data.symbol === '$') {
      return {
        label: categoryName,
        label_value: `${data.targetState - data.currentBaseline >= 0 ? '' : '-'}$${formatWithSuffix(absoluteDifference)}`,
        class_value: 'background-high',
        label_type_value: 'High',
        linear_class: 'line-success',
      };
    } else if (data.symbol === '%') {
      if (Number.isNaN(percentDifference)) {
        return {
          label: categoryName,
          label_value: '0%',
          class_value: 'background-high',
          label_type_value: 'High',
          linear_class: 'line-success',
        };
      }
      return {
        label: categoryName,
        label_value: `${percentDifference >= 0 ? '+' : '-'}${
          Number.isInteger(percentDifference)
            ? Math.abs(percentDifference).toFixed(0)
            : Math.abs(percentDifference).toFixed(2)
        }%`,
        class_value: 'background-high',
        label_type_value: 'High',
        linear_class: 'line-success',
      };
    } else if (data.symbol === '#') {
      const measure =
        data.measure.toLowerCase() === 'full time equivalent' ? 'FTE' : data.measure.toLowerCase();
      return {
        label: categoryName,
        label_value: `${absoluteDifference} ${measure}`,
        class_value: 'background-high',
        label_type_value: 'High',
        linear_class: 'line-success',
      };
    } else {
      return {
        label: categoryName,
        label_value: '---',
        class_value: 'background-none',
        label_type_value: '',
        linear_class: '',
      };
    }
  } catch (error) {
    return {
      label: categoryName,
      label_value: '---',
      class_value: 'background-none',
      label_type_value: '',
      linear_class: '',
    };
  }
}

export function formatToLocalTime(utcString: Date): string {
  // Ensure string ends with Z (to be interpreted as UTC)
  const utcDate = new Date(utcString + 'Z');

  // Convert to user's local timezone
  return utcDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(timestamp: Date | string): string {
  const now = new Date();
  const date = new Date(timestamp + 'Z');

  // If timestamp is a string and doesn't end with Z, add it for UTC parsing
  if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
    date.setTime(new Date(timestamp + 'Z').getTime());
  }

  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  // Less than 1 minute
  if (diffInSeconds < 60) {
    return 'just now';
  }

  // Less than 1 hour
  if (diffInMinutes < 60) {
    return `${diffInMinutes} min ago`;
  }

  // Less than 24 hours
  if (diffInHours < 24) {
    return `${diffInHours} hr ago`;
  }

  // Less than 7 days
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  }

  // More than 7 days - show full date and time
  return date.toLocaleDateString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
// Helper function to remove comment markers from HTML
export const stripCommentMarkers = (html: string): string => {
  return html.replace(/<span[^>]*class="comment-marker"[^>]*>(.*?)<\/span>/g, '$1');
};

export const normalizeHtml = (html: string) =>
  html
    .replace(/\u00A0/g, ' ') // non-breaking space → normal space
    .replace(/\s+/g, ' ') // collapse multiple whitespace
    .trim();

export const canonicalizeHtml = (html: string): string => {
  if (!html || html.trim() === '') return '';

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const container = doc.querySelector('div');

    if (!container) return normalizeHtml(stripCommentMarkers(html));

    // Remove comment-marker spans but preserve their content
    const removeCommentMarkers = (element: Element) => {
      const commentSpans = element.querySelectorAll('span.comment-marker');
      commentSpans.forEach((span) => {
        // Create a document fragment to hold the children
        const fragment = document.createDocumentFragment();
        while (span.firstChild) {
          fragment.appendChild(span.firstChild);
        }
        span.parentNode?.replaceChild(fragment, span);
      });
    };

    removeCommentMarkers(container);

    // Normalize text nodes
    const normalizeTextNodes = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = normalizeHtml(node.textContent || '');
        // Remove empty text nodes
        if (!node.textContent) {
          node.parentNode?.removeChild(node);
          return;
        }
      }

      // Process children (use Array.from to avoid live NodeList issues)
      Array.from(node.childNodes).forEach((child) => {
        normalizeTextNodes(child);
      });
    };

    normalizeTextNodes(container);

    // Sort attributes for consistent serialization
    const sortAttributes = (element: Element) => {
      if (element.hasAttributes()) {
        const attrs = Array.from(element.attributes)
          .map((attr) => [attr.name, attr.value] as [string, string])
          .sort(([a], [b]) => a.localeCompare(b));

        // Clear all attributes
        while (element.attributes.length > 0) {
          element.removeAttribute(element.attributes[0].name);
        }

        // Re-add in sorted order
        attrs.forEach(([name, value]) => {
          element.setAttribute(name, value);
        });
      }

      // Recursively process child elements
      Array.from(element.children).forEach((child) => {
        sortAttributes(child);
      });
    };

    sortAttributes(container);

    // Get the inner HTML and normalize it
    let result = container.innerHTML;

    // Additional normalization
    result = normalizeHtml(result);

    return result;
  } catch (error) {
    console.warn('Error canonicalizing HTML, falling back to simple normalization:', error);
    return normalizeHtml(stripCommentMarkers(html));
  }
};

// Simple hash function for content comparison
const simpleHash = (str: string): string => {
  let hash = 0;
  if (str.length === 0) return hash.toString();
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString();
};

// Extract meaningful text content for comparison
export const extractTextContent = (html: string): string => {
  if (!html) return '';

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const container = doc.querySelector('div');

    if (!container) return '';

    // Remove comment markers but keep their text content
    container.querySelectorAll('span.comment-marker').forEach((span) => {
      const textNode = document.createTextNode(span.textContent || '');
      span.parentNode?.replaceChild(textNode, span);
    });

    // Get text content and normalize whitespace
    let textContent = container.textContent || '';

    // Normalize all whitespace characters
    textContent = textContent
      .replace(/\u00A0/g, ' ') // non-breaking space
      .replace(/\u200B/g, '') // zero-width space
      .replace(/\u2060/g, '') // word joiner
      .replace(/\s+/g, ' ') // collapse whitespace
      .trim();

    return textContent;
  } catch (error) {
    // Fallback: use regex to strip HTML and normalize
    return html
      .replace(/<span[^>]*class="comment-marker"[^>]*>(.*?)<\/span>/g, '$1')
      .replace(/<[^>]+>/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\u200B/g, '')
      .replace(/\u2060/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
};

// Generate content hash for comparison
export const getContentHash = (html: string): string => {
  const textContent = extractTextContent(html);
  return simpleHash(textContent);
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 30) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

export const getFieldTypeLabel = (fieldType: string): string => {
  const fieldTypeLabels: Record<string, string> = {
    current_process: 'Current Process',
    expected_impact: 'Expected Impact',
    ideas_of_improvement: 'Ideas of Improvement',
    key_performance_indicator: 'Key Performance Indicator',
  };
  return fieldTypeLabels[fieldType] || fieldType;
};

export const getFileIcon = (fileName: string, file?: File) => {
  if (!fileName || !file || !Object.keys(file)?.length)
    return <InsertDriveFileIcon sx={{ fontSize: 18, color: '#5f6368' }} />;
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (file && ['jpg', 'jpeg', 'png'].includes(extension || '')) {
    const imageUrl = URL.createObjectURL(file);
    return (
      <Box
        component="img"
        src={imageUrl}
        sx={{
          width: 18,
          height: 18,
          objectFit: 'cover',
          borderRadius: '2px',
        }}
        onLoad={() => URL.revokeObjectURL(imageUrl)}
      />
    );
  }

  // Icon mapping for specific file types only
  switch (extension) {
    case 'pdf':
      return <PictureAsPdfIcon sx={{ fontSize: 18, color: '#d32f2f' }} />;

    // Images
    case 'jpg':
    case 'jpeg':
    case 'png':
      return <ImageIcon sx={{ fontSize: 18, color: '#1A55F9' }} />;

    case 'xml':
      return <CodeIcon sx={{ fontSize: 18, color: '#ff9800' }} />;

    default:
      return <InsertDriveFileIcon sx={{ fontSize: 18, color: '#5f6368' }} />;
  }
};

// File validation constants
// const MAX_FILE_SIZE_KB = 70;
// const MAX_PDF_PAGES = 20;

// File validation function
export const validateFile = async (
  file: File,
  maxSizeMB = 20,
  maxPages = 40
): Promise<{ ok: boolean; error?: string }> => {
  // 1. Validate File Size (Works for all files)
  if (file.size > maxSizeMB * 1024 * 1024) {
    return { ok: false, error: `File must be less than ${maxSizeMB} MB` };
  }

  const ext = file.name.split('.').pop()?.toLowerCase();

  // 2. For PDF → Count Pages
  if (ext === 'pdf') {
    const text = await file.text();
    const pageCount = (text.match(/\/Type\s*\/Page\b/g) || []).length;

    if (pageCount > maxPages) {
      return { ok: false, error: `PDF cannot exceed ${maxPages} pages` };
    }

    return { ok: true };
  }

  // 3. For images → No page concept (optional dimension check)
  if (['jpg', 'jpeg', 'png', 'webp'].includes(ext as any)) {
    return { ok: true }; // no page limit for images by definition
  }

  // 4. For docx / pptx / xlsx → Cannot count pages on frontend
  if (['docx'].includes(ext as any)) {
    return {
      ok: true, // allow them
    };
  }

  // 5. For any other file type
  return { ok: false, error: 'Unsupported file format. supported formats - PDF, JPG, PNG, DOCX.' };
};

export const compareVersions = (a: string, b: string) => {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);

  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};
